import style from "./ViewLesson.module.css";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import previousIcon from "../../../base/assets/icons/arrow_1.svg";
import nextIcon from "../../../base/assets/icons/arrow_next.png";
import statusWaitingIcon from "../../../base/assets/icons/waiting.png";
import LessonDetails, { ListItemsNamesDI } from "./lessonDetails/LessonDetails";
import Subject from "./subject/Subject";
import Lesson from "./lesson/Lesson";
import Docs from "./docs/Docs";
import { useParams } from "react-router";
import AppStore from "../../../base/stores/AppStore";
import store from "../lessonStore";
import { getLessonUrl } from "../services/getLesson"; //
import { LessonDI } from "../types";
import getListItemNamesFromIds from "./utils/getListItemNamesFromIds";
import { Link } from "react-router-dom";
import { formatViewLessonDate } from "../../../base/services/dateTime";
import CustomModal from "../../../base/modal/Modal";
import { useTitle } from "react-use";
import userStore from "../../../base/stores/UserStore";
import Actions from "./Actions";
import ApproveActions from "./ApproveActions";
import Popover from "@mui/material/Popover";
import { observer } from "mobx-react";
import { Box } from "@mui/system";
import ActionsBottom from "./ActionsBottom";
import SubHeader from "../../../base/sub-header/SubHeader";
import NotFound from "../../../base/not-found/NotFound";

/* calc position of popover 'arrow' */
function calcXPosition(el: any) {
  if (!el) return 0;
  return el?.getBoundingClientRect()?.x || 0;
}

const ViewLesson = () => {
  const [lesson, setLesson] = useState<LessonDI>();
  const [listsItemsNames, setListsItemsNames] = useState<ListItemsNamesDI>();
  const { t } = useTranslation();
  const list = store.lists;
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorCode, setErrorCode] = useState(0);
  const user = userStore?.user;
  const isEditor = user?.isEditor || false;
  const [isExistLesson, setIsExistLesson] = useState<boolean>(true);
  let { lessonUrl } = useParams<{ lessonUrl: string }>();
  useEffect(() => {
    // Component mounted with lessonUrl
  }, [lessonUrl]);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handlePopoverShowClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id_ = open ? "simple-popover" : undefined;

  useTitle(`${t("General-content-header")} - ${lesson ? lesson.title : ""}`);

  useEffect(() => {
    fetchLesson();
  }, [lessonUrl]);

  const handleModalStatus = (errorMode: boolean) => {
    setError(errorMode);
  };

  const tags = useMemo(() => {
    const t: Array<string> = [];
    for (let prop in listsItemsNames) {
      if (typeof listsItemsNames[prop] === "string") {
        t.push(listsItemsNames[prop] as string);
      } else if (Array.isArray(listsItemsNames[prop])) {
        for (let str of listsItemsNames[prop]) {
          t.push(str);
        }
      }
    }
    return t;
  }, [listsItemsNames]);

  const getListsItemsNames = (lesson: LessonDI) => {
    try {
      const partners = getListItemNamesFromIds(
        list.projectPartnerIds,
        lesson.projectPartnerIds
      );
      const lessonTags = getListItemNamesFromIds(
        list.lessonTags,
        lesson.lessonTags
      );
      const field = list.fieldId.filter((i) => i.id == lesson.fieldId)[0].name;
      const type = list.projectTypeId.filter(
        (i) => i.id == lesson.projectTypeId
      )[0].name;
      const step = list.stepId.filter((i) => i.id == lesson.stepId)[0].name;
      const external = list.externalFactorInvolvedId.filter(
        (i) => i.id == lesson.externalFactorInvolvedId
      )[0]?.name;
      const organizational = list.organizationalUnitId.filter(
        (i) => i.id == lesson.organizationalUnitId
      )[0]?.name;
      return {
        partners,
        lessonTags,
        field,
        type,
        step,
        external,
        organizational,
      };
    } catch (error) {
      alert(error);
    }
  };

  const fetchLesson = async () => {
    if (!lessonUrl) {
      setErrorMessage(t("Error-InvalidLessonUrl"));
      return;
    }
  
    try {
      AppStore.incrementLoadingProcedure();
      const existLesson = await getLessonUrl(lessonUrl);
      if (!existLesson) return setIsExistLesson(false);

      setIsExistLesson(true);
      setLesson(existLesson);
      setListsItemsNames(getListsItemsNames(existLesson));
    } catch (error: any) {
      if (error.statusCode !== 0) setIsExistLesson(false);
      if (error.statusCode < 400) {
        setErrorMessage(error.errorMessages);
      } else {
        setErrorMessage(t("GeneralError"));
      }
      setErrorCode(error.statusCode);
      setError(true);
    } finally {
      AppStore.decrementLoadingProcedure();
    }
  };

  return (
    <>
      {isExistLesson ? (
        <>
          {lesson && (
            <div className={style.container}>
              <SubHeader
                links={lesson.breadCrumbs || []}
                showBackgroundImage={false}
              />
              <header className={style.headline}>
                <div className={style.textAndOptions}>
                  <h1 tabIndex={0}>{lesson?.title}</h1>
                  <div className={`${style.editIconWrapper} ${style.desktopActions}`}>
                    <Actions lesson={lesson} />
                  </div>
                </div>
                {lesson.total && lesson?.total > 1 && (
                  <div className={style.nextOrPrevious}>
                    <Link
                      className={style.previous}
                      to={`${AppStore.lessonsRoute}${lesson?.prev}`}
                    >
                      <span>{t("lesson-view-previous")}</span>
                      <img src={previousIcon} alt={t("lesson-view-previous")} />
                    </Link>
                    <Link
                      className={style.next}
                      to={`${AppStore.lessonsRoute}${lesson?.next}`}
                    >
                      <span>{t("lesson-view-next")}</span>
                      <img src={nextIcon} alt={t("lesson-view-next")} />
                    </Link>
                  </div>
                )}
              </header>
              <div tabIndex={1} className={style.dateAndStatus}>
                <p className={style.date}>
                  {lesson?.author || t("Lesson-view-anonymous-author")},{" "}
                  {t("Update-at")}
                  {lesson.lastUpdate ? formatViewLessonDate(lesson.lastUpdate) : ""}
                </p>
                {!lesson.isApproved && (
                  <img
                    src={statusWaitingIcon}
                    alt={t("lesson-view-pending")}
                    className={style.statusIcon}
                  />
                )}
              </div>
              <div className={style.filter}>
                {tags.map((tag) => {
                  return (
                    <div
                      aria-label={tag}
                      tabIndex={1}
                      key={tag}
                      className={style.filterItem}
                    >
                      <p>{tag}</p>
                    </div>
                  );
                })}
                {/* popover */}
                <span
                  onClick={handlePopoverShowClick}
                  aria-label={t("Lesson-Plus-Icon-alt")}
                  className={style.plus}
                >
                  <p>{tags.length - 2} </p>
                  <p className={style.plusPlus}>+</p>
                </span>
                <Popover
                  id={id_}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handlePopoverClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  PaperProps={{
                    style: {
                      overflow: "visible",
                      color: "rgba(112, 112, 112, 1)",
                      width: "88%",
                      margin: "auto -5px",
                    },
                  }}
                >
                  <Box>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        padding: "3px 5px",
                      }}
                    >
                      <button
                        onClick={handlePopoverClose}
                        style={{ opacity: "0.5" }}
                      >
                        x
                      </button>
                    </div>
                    <div className={style.popoverTagWrap}>
                      {tags.slice(2).map((tag) => {
                        return (
                          <div
                            aria-label={tag}
                            tabIndex={1}
                            key={tag}
                            className={style.popoverTag}
                          >
                            {tag}
                          </div>
                        );
                      })}
                    </div>
                  </Box>
                </Popover>
              </div>
              <div className={style.details}>
                {listsItemsNames && (
                  <LessonDetails listItemsNames={listsItemsNames} />
                )}
              </div>
              <div className={style.subject}>
                <Subject subject={lesson.subjectText} />
              </div>
              <div className={style.lesson}>
                <Lesson lessonText={lesson.lessonText} />
              </div>
              {/* <div className={style.docs}> // 17.3.22 : disabled docs from lessons as requested remove comment to restore
                <Docs docs={lesson.docs} />
              </div> */}
              <div className={style.mobileActions}>
                <ActionsBottom lesson={lesson} />
              </div>
            </div>
          )}
          { isEditor && (
            <ApproveActions lessonId={lesson?.id} />
          )}
          {error ? (
            <CustomModal
              text={errorMessage}
              isOpen={error}
              handleModalStatus={handleModalStatus}
            />
          ) : null}
        </>
      ) : (
        <NotFound />
      )}
    </>
  );
};

export default observer(ViewLesson);
